import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { fetchMe, type MeResponse } from '../api/me';
import { productCatalogue } from '../catalogue/products';
import { NamePrompt } from '../components/NameGate';
import { useNameGate } from '../hooks/useNameGate';
import { resolvePostSigninDestination } from '../lib/routing';
import { ENTERPRISE_URL } from '../lib/scenarioNav';
import { isOrgAdmin, isCloudhouseAdmin } from '../lib/permissions';
import { canOpen } from '../lib/accessState';
import { clearReturnPath, peekReturnPath } from '../lib/returnPath';
import { peekTrialIntent } from '../lib/trialIntent';

// Anyone with hub-only responsibilities stays on the hub: org admins (manage organisation,
// trials) and Cloudhouse staff (the staff console). Only plain members bypass to their product.
// Shares the one isOrgAdmin definition with the terms gate, so the bypass can never diverge and
// let an un-accepted admin skip /terms (EIT-140).
// eslint-disable-next-line react-refresh/only-export-components
export function isHubBound(permissions: readonly string[] | null | undefined): boolean {
  return isOrgAdmin(permissions) || isCloudhouseAdmin(permissions);
}

// eslint-disable-next-line react-refresh/only-export-components
export function getBypassUrl(
  me: MeResponse,
  hubBound: boolean,
  roles: readonly string[] | null | undefined,
): string | null {
  const active = Object.entries(me.products).filter(([, e]) => e?.status === 'active');
  if (active.length !== 1 || hubBound) return null;
  const [[key, entitlement]] = active;
  if (!canOpen(key, entitlement, roles)) return null;
  return productCatalogue.find(p => p.key === key)?.path ?? null;
}

export function PostSignin() {
  const auth = useAuth();
  const [destination, setDestination] = useState<string | null>(null);
  const [bypassUrl, setBypassUrl] = useState<string | null>(null);

  // 'pending' (auth resolving) and 'welcome' (settled zero-org) are decided
  // synchronously and handled in render below — no setState in the effect. Only
  // the 'resolve' path (org user → /api/me bypass) needs the async effect.
  const destinationKind = resolvePostSigninDestination({
    isLoading: auth.isLoading,
    organizationId: auth.organizationId ?? null,
  });

  // NameGate wraps the hub routes, but a single-product member leaves for their product from here
  // without rendering any of them — so the same check runs before the bypass. Zero-org users are
  // not checked: /welcome collects their names with the organisation. Flag off or not yet known:
  // open at once, so the bypass is never held on LaunchDarkly. Nothing renders here while open, so
  // a flag that reads on before the bypass is decided may still check.
  const nameGate = useNameGate({ enabled: destinationKind === 'resolve', stayOpen: false });

  // A page the visitor was sent to sign in from wins over the landing page and the bypass, but not
  // over a trial sign-up: the trial starts on the hub.
  const [returnPath] = useState(() => (peekTrialIntent() ? null : peekReturnPath()));

  useEffect(() => {
    if (destinationKind !== 'resolve' || returnPath) return;
    const ctrl = new AbortController();
    void (async () => {
      const getToken = (auth as { getAccessToken?: () => Promise<string> }).getAccessToken;
      const token = getToken ? await getToken() : null;
      if (!token) {
        setDestination(ENTERPRISE_URL);
        return;
      }
      try {
        const me = await fetchMe(token, ctrl.signal);
        const authState = auth as { permissions?: string[]; roles?: string[] | null };
        const bypass = getBypassUrl(me, isHubBound(authState.permissions), authState.roles);
        if (bypass) {
          setBypassUrl(bypass);
          return;
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
      }
      setDestination(ENTERPRISE_URL);
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth.getAccessToken is read once when the org-user flow runs
  }, [destinationKind]);

  useEffect(() => {
    if (destinationKind !== 'pending') clearReturnPath();
  }, [destinationKind]);

  useEffect(() => {
    if (!bypassUrl || nameGate.status !== 'open') return;
    globalThis.location.href = bypassUrl;
  }, [bypassUrl, nameGate.status]);

  if (destinationKind === 'pending') return null;
  if (destinationKind === 'welcome') return <Navigate to="/welcome" replace />;
  if (nameGate.status === 'prompt') return <NamePrompt form={nameGate.form} onSignOut={() => auth.signOut()} />;
  // Hub-bound users wait here too, so the hub's own gate finds the name already handled.
  const target = returnPath ?? destination;
  if (nameGate.status === 'waiting' || bypassUrl || !target) return null;
  return <Navigate to={target} replace />;
}
